<template>
  <div>
    <!-- Hidden trigger — opened programmatically via openModal() so the
         parent only needs to call `this.$refs.refundModal.open(tx)`. -->
    <button
      style="display: none"
      type="button"
      :class="triggerClass"
      data-bs-toggle="modal"
      :data-bs-target="`#${modalId}`"
    ></button>

    <div
      class="modal fade"
      :id="modalId"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content refund-modal">
          <div class="modal-header refund-modal__header">
            <div class="d-flex align-items-center">
              <span class="refund-modal__icon">
                <i class="material-icons">undo</i>
              </span>
              <div>
                <h5 class="modal-title mb-0">{{ isAllinpay ? 'Void Card Payment' : 'Refund Transaction' }}</h5>
                <small class="text-secondary">{{ isAllinpay ? 'Cancel the card payment on the kiosk terminal.' : "Return funds to the member's original payment method." }}</small>
              </div>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body refund-modal__body">
            <!-- Transaction summary -->
            <div class="refund-summary">
              <div class="refund-summary__row">
                <span class="refund-summary__label">Transaction ID</span>
                <span class="refund-summary__value">{{ transaction.transaction_id || '—' }}</span>
              </div>
              <div class="refund-summary__row">
                <span class="refund-summary__label">Receipt No</span>
                <span class="refund-summary__value">{{ transaction.receipt_no || '—' }}</span>
              </div>

              <div class="refund-summary__amounts">
                <div class="refund-amount-card">
                  <span class="refund-amount-card__label">Paid</span>
                  <span class="refund-amount-card__value">{{ formatMoney(paidAmount) }}</span>
                </div>
                <div class="refund-amount-card refund-amount-card--muted">
                  <span class="refund-amount-card__label">Already Refunded</span>
                  <span class="refund-amount-card__value">{{ formatMoney(alreadyRefunded) }}</span>
                </div>
                <div class="refund-amount-card refund-amount-card--highlight">
                  <span class="refund-amount-card__label">Refundable</span>
                  <span class="refund-amount-card__value">{{ formatMoney(refundableRemaining) }}</span>
                </div>
              </div>
            </div>

            <!-- Amount input -->
            <div class="refund-field">
              <label class="refund-field__label">
                {{ isAllinpay ? 'Void Amount' : 'Refund Amount' }} <span class="text-danger">*</span>
              </label>
              <div class="refund-input-group" :class="{ 'is-invalid': refundAmount !== '' && !isRefundAmountValid }">
                <span class="refund-input-group__prefix">$</span>
                <input
                  type="number"
                  class="refund-input-group__input"
                  v-model.number="refundAmount"
                  :max="refundableRemaining"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  :disabled="isAllinpay"
                />
                <button
                  v-if="!isAllinpay"
                  type="button"
                  class="refund-input-group__max"
                  @click="refundAmount = refundableRemaining"
                >Max</button>
              </div>
              <!-- Allinpay: a card payment is only ever voided in full -->
              <div class="refund-quick-amounts" v-if="!isAllinpay">
                <button
                  type="button"
                  v-for="pct in [25, 50, 75, 100]"
                  :key="pct"
                  class="refund-chip"
                  :class="{ 'refund-chip--active': isQuickAmountActive(pct) }"
                  @click="setQuickAmount(pct)"
                >{{ pct }}%</button>
              </div>
              <small v-if="refundAmount !== '' && !isRefundAmountValid" class="text-danger">
                Amount must be between $0.01 and {{ formatMoney(refundableRemaining) }}.
              </small>
              <small v-if="hasAirwallexSummary && isRefundAmountValid" class="d-block text-secondary">
                Taken from your Airwallex balance: {{ formatMoney(Number(refundAmount) + refundFee) }}<span v-if="refundFee > 0"> (refund {{ formatMoney(refundAmount) }} + refund fee {{ formatMoney(refundFee) }})</span>.
              </small>
            </div>

            <!-- Reason (free-text, optional). Airwallex only: Allinpay takes none. -->
            <div class="refund-field" v-if="!isAllinpay">
              <label class="refund-field__label">
                Reason <span class="text-secondary">(optional)</span>
              </label>
              <input
                type="text"
                class="refund-field__input"
                v-model="refundReason"
                maxlength="128"
                placeholder="e.g. Customer changed their mind"
              />
            </div>

            <!-- What the last press got back: kept in the modal so it can be read. -->
            <div
              v-if="alert"
              class="refund-alert"
              :class="alert.type === 'danger' ? 'refund-alert--danger' : 'refund-alert--warning'"
              role="alert"
            >
              <i class="material-icons refund-alert__icon">{{ alert.type === 'danger' ? 'error_outline' : 'hourglass_top' }}</i>
              <div>
                <b class="refund-alert__title">{{ alert.title }}</b>
                <span>{{ alert.text }}</span>
              </div>
            </div>
            <div class="refund-note" v-if="isAllinpay && transaction.allinpay_refund_pending">
              <i class="material-icons refund-note__icon">hourglass_top</i>
              <span>A refund on this order is still being confirmed by the terminal. Refund checks it first and sends nothing new.</span>
            </div>
            <div class="refund-note" v-if="isAllinpay">
              <i class="material-icons refund-note__icon">info</i>
              <span>Voids the full amount on the kiosk's card terminal, which must be switched on: only today, before the terminal is settled. Someone must press OK on the terminal within 60 seconds. After that, or for part of it, refund it in Allinpay OATS or with Allinpay's manual refund form.</span>
            </div>
            <div class="refund-note" v-else>
              <i class="material-icons refund-note__icon">info</i>
              <span>Funds are returned to the original payment method. Card refunds may take 5–10 business days to appear.</span>
            </div>
          </div>

          <div class="modal-footer refund-modal__footer">
            <button
              type="button"
              class="btn btn-primary btn-sm refund-modal__submit"
              :disabled="!isRefundAmountValid || submitting"
              @click="submit"
            >
              <i class="material-icons" v-if="!submitting">undo</i>
              <span v-if="submitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ submitting ? 'Processing…' : (isPendingAlert ? 'Check status' : `${isAllinpay ? 'Void' : 'Refund'} ${isRefundAmountValid ? formatMoney(refundAmount) : ''}`.trim()) }}
            </button>
            <button
              type="button"
              class="btn btn-light btn-sm"
              :class="closeTriggerClass"
              data-bs-dismiss="modal"
              :disabled="submitting"
            >Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import PaymentService from "../../../services/payment.service";

export default {
  name: "RefundModal",
  props: {
    /**
     * MerchantCode passed in the request header. Required.
     */
    merchantCode: {
      type: String,
      required: true,
    },
    /**
     * Unique DOM id for the underlying Bootstrap modal. Override only when
     * embedding multiple instances on the same page.
     */
    modalId: {
      type: String,
      default: "modalRefundTransaction",
    },
  },
  emits: ["refunded", "error"],
  data() {
    return {
      transaction: {},
      refundAmount: "",
      refundReason: "",
      submitting: false,
      // { type: 'danger' | 'warning', title, text } from the last submit
      alert: null,
    };
  },
  computed: {
    triggerClass() {
      return `trigger-${this.modalId}`;
    },
    closeTriggerClass() {
      return `close-${this.modalId}`;
    },
    // Kiosk order paid by card on its Allinpay terminal (set by the BE).
    isAllinpay() {
      return !!this.transaction.allinpay_card;
    },
    // Airwallex row with the BE's refund summary (RefundSummary).
    hasAirwallexSummary() {
      return !this.isAllinpay && this.transaction.airwallex_refundable !== undefined;
    },
    paidAmount() {
      // Airwallex: what the intent charged (payment_amount + BCRS), not the bill total.
      if (this.hasAirwallexSummary) return Number(this.transaction.airwallex_paid || 0);
      // Allinpay: what the card paid, not the bill total.
      return Number((this.isAllinpay ? this.transaction.payment_amount : this.transaction.total_amount) || 0);
    },
    alreadyRefunded() {
      // Airwallex: refunds still processing or being checked count too, they can go through.
      if (this.hasAirwallexSummary) {
        return Number(this.transaction.airwallex_refunded || 0)
          + Number(this.transaction.airwallex_refund_processing || 0)
          + Number(this.transaction.airwallex_refund_checking || 0);
      }
      return Number(this.transaction.total_refund_amount || 0);
    },
    refundableRemaining() {
      if (this.hasAirwallexSummary) return Number(this.transaction.airwallex_refundable || 0);
      return Math.max(Number((this.paidAmount - this.alreadyRefunded).toFixed(2)), 0);
    },
    // Airwallex takes the refund plus the merchant's refund fee from its balance.
    refundFee() {
      return this.hasAirwallexSummary ? Number(this.transaction.airwallex_refund_fee || 0) : 0;
    },
    isPendingAlert() {
      return !!this.alert && this.alert.type === "warning";
    },
    isRefundAmountValid() {
      const amount = Number(this.refundAmount);
      return amount > 0 && amount <= this.refundableRemaining;
    },
  },
  methods: {
    /**
     * Public API — open the modal for a given transaction.
     * Usage: this.$refs.refundModal.open(transaction)
     */
    open(transaction) {
      this.transaction = transaction || {};
      this.refundAmount = this.refundableRemaining;
      this.refundReason = "";
      this.submitting = false;
      this.alert = null;
      this.$nextTick(() => {
        $(`.${this.triggerClass}`).click();
      });
    },
    close() {
      // Bootstrap 5 native (data-bs-dismiss="modal") handles the hide when
      // the Cancel button is clicked. Two race conditions to defend against:
      //   1. The Cancel button has :disabled="submitting" — caller must flip
      //      submitting=false BEFORE close() (submit() already does this).
      //   2. Even after the JS flip, Vue queues the DOM re-render for the
      //      next tick. If we click immediately, the button still carries
      //      the `disabled` attribute in the DOM → click is a no-op. Wait
      //      one tick so the attribute is gone before triggering the click.
      this.$nextTick(() => {
        $(`.${this.closeTriggerClass}`).click();
      });
    },
    setQuickAmount(pct) {
      this.refundAmount = Number((this.refundableRemaining * pct / 100).toFixed(2));
    },
    isQuickAmountActive(pct) {
      const target = Number((this.refundableRemaining * pct / 100).toFixed(2));
      return Number(this.refundAmount) === target;
    },
    formatMoney(value) {
      return "$ " + Number(value || 0).toFixed(2);
    },
    async submit() {
      if (!this.isRefundAmountValid || this.submitting) return;
      this.submitting = true;
      this.alert = null;
      // BE resolves the Airwallex payment_intent_id from the transaction's
      // airwallex_order_id column — FE only needs to identify the transaction.
      const payload = {
        transaction_id: this.transaction.transaction_id,
        amount: Number(this.refundAmount),
        // Send `reason` only when provided; BE treats missing field as default.
        ...(this.refundReason && this.refundReason.trim()
          ? { reason: this.refundReason.trim() }
          : {}),
      };

      // Capture the outcome inside the try/catch, then emit AFTER the block
      // settles. If we $emit inside try, a synchronous error thrown by the
      // parent listener (e.g. getMyTransaction() failing) bubbles up here
      // and gets caught — making a successful refund look like a failure
      // ("Refund failed" snackbar despite the API returning 200).
      let refundData = null;
      let errorMessage = null;
      let message = null;
      let pendingMessage = null;
      try {
        const response = this.isAllinpay
          ? await PaymentService.allinpayRefund({ transaction_id: payload.transaction_id, amount: payload.amount }, this.merchantCode)
          : await PaymentService.airwallexRefund(payload, this.merchantCode);
        if (response.data.result.isSuccess) {
          refundData = response.data.data;
          // Allinpay answers with the terminal's verdict.
          if (this.isAllinpay) {
            const state = refundData?.state;
            if (state === "DECLINED") {
              errorMessage = refundData.message || "The terminal declined it.";
              refundData = null;
            } else if (state === "PENDING" || state === "UNKNOWN") {
              pendingMessage = refundData.message || "Sent to the terminal, not confirmed yet. Finish any prompt on the terminal, then check again in a minute.";
              refundData = null;
            } else {
              // A refund sent after a declined void says so in its message.
              message = refundData.message || (refundData.type === "VOID" ? "Payment voided" : "Refund successful");
            }
          } else if (refundData?.status === "PROCESSING") {
            // Airwallex received it but can still fail it; the BE settles it
            // and sends the money back to the balance if it does.
            message = refundData.message || "Refund submitted. Airwallex confirms it within a day; if it fails, the amount goes back to your Airwallex balance.";
          }
        } else if (this.isAllinpay && response.data.data?.error_code === "NO_ANSWER") {
          pendingMessage = response.data.result.message;
        } else {
          errorMessage = response.data.result.message || "Refund failed";
        }
      } catch (error) {
        const answered = error.response?.data?.result?.message;
        if (answered) {
          errorMessage = answered;
        } else {
          // No answer (timeout, gateway error) is not a failure: the
          // terminal / Airwallex may have given the money back.
          pendingMessage = "No answer yet - the refund may still be running. Refresh the order in a minute before trying again.";
        }
      }

      this.submitting = false;

      if (pendingMessage) {
        this.alert = { type: "warning", title: "Not confirmed yet", text: pendingMessage };
        return;
      }

      if (errorMessage) {
        this.alert = { type: "danger", title: this.isAllinpay ? "Money not given back" : "Refund failed", text: errorMessage };
      }

      if (refundData) {
        this.close();
        this.$emit("refunded", {
          transaction: this.transaction,
          amount: payload.amount,
          data: refundData,
          message,
        });
      } else {
        this.$emit("error", errorMessage);
      }
    },
  },
};
</script>

<style scoped>
.refund-alert {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.5;
}

.refund-alert__icon {
  font-size: 20px;
  flex-shrink: 0;
}

.refund-alert__title {
  display: block;
  margin-bottom: 2px;
}

.refund-alert--danger {
  background: #fdecea;
  color: #8a1c13;
  border: 1px solid #f5c2bd;
}

.refund-alert--warning {
  background: #fff6e0;
  color: #7a4f00;
  border: 1px solid #f3dca2;
}

.refund-modal {
  border: none;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.refund-modal__header {
  padding: 20px 24px;
  border-bottom: 1px solid #eef0f4;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  align-items: flex-start;
}

.refund-modal__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #eef2ff;
  color: #4f46e5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.refund-modal__icon .material-icons {
  font-size: 22px;
}

.refund-modal__header .modal-title {
  font-size: 17px;
  font-weight: 600;
  color: #0f172a;
}

.refund-modal__header small {
  font-size: 12px;
  color: #64748b;
}

.refund-modal__body {
  padding: 22px 24px;
  background: #ffffff;
}

.refund-summary {
  background: #f8fafc;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 22px;
}

.refund-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 4px 0;
}

.refund-summary__label {
  color: #64748b;
}

.refund-summary__value {
  color: #0f172a;
  font-weight: 500;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12.5px;
}

.refund-summary__amounts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 12px;
}

.refund-amount-card {
  background: #ffffff;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.refund-amount-card__label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.refund-amount-card__value {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

.refund-amount-card--muted .refund-amount-card__value {
  color: #94a3b8;
}

.refund-amount-card--highlight {
  background: #ecfdf5;
  border-color: #bbf7d0;
}

.refund-amount-card--highlight .refund-amount-card__value {
  color: #047857;
}

.refund-amount-card--highlight .refund-amount-card__label {
  color: #059669;
}

.refund-field {
  margin-bottom: 18px;
}

.refund-field__label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  margin-bottom: 8px;
}

.refund-field__input {
  width: 100%;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  color: #0f172a;
  background: #ffffff;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.refund-field__input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.refund-input-group {
  display: flex;
  align-items: stretch;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: #ffffff;
}

.refund-input-group:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.refund-input-group.is-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

.refund-input-group__prefix {
  padding: 0 14px;
  display: flex;
  align-items: center;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  border-right: 1px solid #e2e8f0;
}

.refund-input-group__input {
  border: none;
  outline: none;
  padding: 10px 12px;
  flex: 1;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  background: transparent;
}

.refund-input-group__input::-webkit-outer-spin-button,
.refund-input-group__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.refund-input-group__max {
  border: none;
  background: transparent;
  color: #4f46e5;
  font-weight: 600;
  font-size: 12px;
  padding: 0 14px;
  cursor: pointer;
  border-left: 1px solid #e2e8f0;
  transition: background 0.15s ease;
}

.refund-input-group__max:hover {
  background: #eef2ff;
}

.refund-quick-amounts {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.refund-chip {
  flex: 1;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.refund-chip:hover {
  border-color: #6366f1;
  color: #4f46e5;
}

.refund-chip--active {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #ffffff;
}

.refund-note {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 12.5px;
  color: #92400e;
  line-height: 1.5;
}

.refund-note__icon {
  font-size: 18px !important;
  color: #d97706;
  flex-shrink: 0;
  margin-top: 1px;
}

.refund-modal__footer {
  padding: 16px 24px 20px;
  border-top: 1px solid #eef0f4;
  background: #ffffff;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.refund-modal__footer .btn {
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
}

.refund-modal__submit {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.refund-modal__submit:hover:not(:disabled) {
  background: #4338ca;
  border-color: #4338ca;
  color: #ffffff;
}

.refund-modal__submit:disabled {
  background: #c7d2fe;
  border-color: #c7d2fe;
  color: #ffffff;
  opacity: 1;
  cursor: not-allowed;
}

.refund-modal__submit .material-icons {
  font-size: 16px;
}
</style>
